import type { MutableRefObject, RefObject } from 'react';
import { useCallback, useMemo } from 'react';

import type { QueryRequest } from '@rtkwlf/asset-data-service';
import isEmpty from 'lodash/isEmpty';
import { useQueryParam, withDefault } from 'use-query-params';

import type {
  DataGridProProps,
  GridApiPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
} from '@packages/fenrir/components/datagrid';
import {
  getTogglableColumnsDefault,
  type GridSortModel,
} from '@packages/fenrir/components/datagrid';
import { useGridPaginationModel } from '@packages/hooks/use-grid-pagination-model/use-grid-pagination-model';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';
import { DataGridProSortParam } from '@packages/utils/use-query-params/data-grid-pro-sort-param/data-grid-pro-sort-param';

import { paginationConfig } from '../../../assets/assets.constants';
import type { Filters } from '../../../assets/device-list/asset-filters/filters';
import { useAssetFilters } from '../../../assets/device-list/asset-filters/use-asset-filters/use-asset-filters';
import { useAssetTypeParam } from '../../../assets/device-list/use-asset-type-param';
import type { AssetsSnackbarRef } from '../../assets-snackbar/assets-snackbar';
import { type AssetsTableColumnId } from '../asset-table.constants';
import { getInitialSortModel } from '../get-initial-sort-model/get-initial-sort-model';
import { updateGridStateToSession } from '../update-data-grid-state';
import { useAssetsParams } from '../use-assets-params/use-assets-params';
import { useAssetsTableColumns } from '../use-assets-table-columns/use-assets-table-columns';
import { useColumnVisibilityModel } from '../use-column-visibility-model/use-column-visibility-model';
import { useDevicesEnabledColumns } from '../use-devices-enabled-columns/use-device-enabled-columns';
import { useSavedAssetTableColumns } from '../use-saved-asset-table-columns/use-saved-asset-table-columns';

export type assetTableConfig = {
  initialState: DataGridProProps['initialState'];
  apiRef: MutableRefObject<GridApiPro>;
  sortModel: GridSortModel;
  onSortModelChange: (newSortModel: GridSortModel) => void;
  setFilterModel: (newFilterModel: GridFilterModel) => void;
  applyFilters: () => void;
  assetListParams: QueryRequest;
  paginationModel: {
    page: number;
    pageSize: number;
  };
  filters?: Filters;
  filterModel?: GridFilterModel;
  columns: GridColDef[];
  setPaginationModel: ({
    page,
    pageSize,
  }: Partial<GridPaginationModel>) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (
    newVisibilityModel: GridColumnVisibilityModel,
  ) => void;
};

/**
 * NOTE: Not using fenrir's usePersistedGrid because:
 * No current way to override config stored in storageManager
 * Need to set config based on queryParams prop as top priority
 */
export const useAssetTableConfig = (
  snackbarRef?: RefObject<AssetsSnackbarRef>,
): assetTableConfig => {
  const assetType = useAssetTypeParam();
  const [isFenrirV2FeatureEnabled] = useFeatureFlag('ADS_FENRIR_V2');
  const { paginationModel, setPaginationModel } = useGridPaginationModel({
    pageSize: paginationConfig.assetsList.pageSize,
  });

  const { gridAPIRef, filters, filterModel, setFilterModel, applyFilters } =
    useAssetFilters(snackbarRef);

  // Sort model logic
  const [sortModel, setSortModel] = useQueryParam(
    'sort',
    withDefault(
      DataGridProSortParam,
      getInitialSortModel({ assetType, isFenrirV2FeatureEnabled }),
    ),
  );

  const handleSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      setSortModel(newSortModel);
      setPaginationModel({ page: 0 });

      if (isFenrirV2FeatureEnabled) {
        // Save the grid state to session storage
        const gridState = gridAPIRef.current.exportState();
        if (!gridState.sorting) {
          gridState.sorting = {};
        }
        gridState.sorting.sortModel = newSortModel;
        updateGridStateToSession(gridState, assetType);
      }
    },
    [
      gridAPIRef,
      assetType,
      setPaginationModel,
      setSortModel,
      isFenrirV2FeatureEnabled,
    ],
  );
  // Sort model logic ends

  const assetListParams = useAssetsParams({
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    sort: sortModel,
    filters,
  });

  // Columns logic starts
  const selectedColumns = useDevicesEnabledColumns();
  const columns = useAssetsTableColumns(selectedColumns);
  const togglableColumns = useMemo(
    () => getTogglableColumnsDefault(columns) as AssetsTableColumnId[],
    [columns],
  );
  const optionalColumns: AssetsTableColumnId[] = useMemo(
    () =>
      isFenrirV2FeatureEnabled
        ? ['hostname', 'os', 'ipAddress', 'mac', 'netbios', 'osType']
        : [],
    [isFenrirV2FeatureEnabled],
  );

  const { savedAssetTableColumns, setSavedAssetTableColumns } =
    useSavedAssetTableColumns(togglableColumns, optionalColumns);

  const { columnVisibilityModel, setColumnVisibilityModel } =
    useColumnVisibilityModel({
      togglableColumns,
      selectedColumns: savedAssetTableColumns,
      setSelectedColumns: setSavedAssetTableColumns,
    });
  // Columns logic ends

  /**
   * Wrapper on applyFilters to check if the column being filtered is visible before applying the filter
   * if the column is hidden - updates column visibility for the column and then applies the filters
   * if the column is visible - applies the filters
   * @returns {void}
   */
  const updateColumnVisibilityAndApplyFilters = useCallback(() => {
    const newColumnVisibilityModel = { ...columnVisibilityModel };
    let hiddenColumnsFound = false;

    const filterModel = gridAPIRef.current?.exportState()?.filter
      ?.filterModel || { items: [] };

    filterModel?.items?.forEach((filter) => {
      if (!newColumnVisibilityModel[filter.field] && !isEmpty(filter.value)) {
        newColumnVisibilityModel[filter.field] = true;
        hiddenColumnsFound = true;
      }
    });

    if (hiddenColumnsFound) {
      setColumnVisibilityModel(newColumnVisibilityModel);
    }
    applyFilters();
  }, [
    applyFilters,
    columnVisibilityModel,
    gridAPIRef,
    setColumnVisibilityModel,
  ]);

  const initialState: DataGridProProps['initialState'] = useMemo(
    () => ({
      pinnedColumns: { right: ['actions'] },
      filter: {
        filterModel,
      },
    }),
    [filterModel],
  );

  return {
    initialState,
    apiRef: gridAPIRef,
    sortModel,
    onSortModelChange: handleSortModelChange,
    paginationModel,
    setPaginationModel,
    filters,
    filterModel,
    setFilterModel,
    applyFilters: updateColumnVisibilityAndApplyFilters,
    assetListParams,
    columns,
    columnVisibilityModel,
    setColumnVisibilityModel,
  };
};
